import {
  MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS,
  parseMarketplaceBriefSnapshot,
  type MarketplaceBriefSnapshot,
  type MarketplaceOrderHandoffDefaults,
} from "@nexus/contracts";

import { isUsableScriptText } from "@/features/opportunities/lib/script-display-title";

import type { OrderScriptRow } from "../types/marketplace.types";

export interface OrderScriptHandoffField {
  key: keyof MarketplaceOrderHandoffDefaults;
  value: string;
}

export interface OrderScriptDisplay {
  title: string;
  /** Opening line. Same as `hook`; kept so list rows do not rename. */
  subtitle: string | null;
  hook: string | null;
  synopsis: string | null;
  fullScript: string | null;
  format: string | null;
  duration: string | null;
  opportunitySummary: string | null;
  briefUrl: string | null;
  uploadId: string | null;
  /** Imported rows that only carry an external brief/asset — no Nexus script behind them. */
  briefOnly: boolean;
  /** First-class persona brief line (immutable snapshot). */
  personaBrief: MarketplaceBriefSnapshot | null;
  personaName: string | null;
  orderInstructions: string | null;
  handoffFields: OrderScriptHandoffField[];
}

const SHORT_TITLE_MAX_CHARS = 80;
const COLON_TITLE_MIN_CHARS = 3;
const COLON_TITLE_MAX_CHARS = 60;

function snapshotString(
  snapshot: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = snapshot?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function distinct(value: string | null, other: string | null): string | null {
  if (!value) return null;
  if (other && value === other) return null;
  return value;
}

function snapshotHandoffFields(
  snapshot: Record<string, unknown> | null,
): OrderScriptHandoffField[] {
  return MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.flatMap((item) => {
    const value = snapshotString(snapshot, item.key);
    return value ? [{ key: item.key, value }] : [];
  });
}

function snapshotPersonaName(
  snapshot: Record<string, unknown> | null,
): string | null {
  return (
    snapshotString(snapshot, "personaName") ??
    snapshotString(snapshot, "persona")
  );
}

function snapshotOrderInstructions(
  snapshot: Record<string, unknown> | null,
  usedElsewhere: ReadonlyArray<string | null>,
): string | null {
  const value =
    snapshotString(snapshot, "orderInstructions") ??
    snapshotString(snapshot, "instructions") ??
    snapshotString(snapshot, "handoffOverrideNote");
  if (!value) return null;
  return usedElsewhere.includes(value) ? null : value;
}

/**
 * Concept is often stored as "Short title: longer synopsis paragraph".
 * Split that so the detail panel can label the fields correctly.
 */
export function splitConceptTitle(concept: string): {
  title: string;
  synopsis: string | null;
} {
  const colon = concept.indexOf(": ");
  if (
    colon >= COLON_TITLE_MIN_CHARS &&
    colon <= COLON_TITLE_MAX_CHARS &&
    concept.length > colon + 2
  ) {
    return {
      title: concept.slice(0, colon).trim(),
      synopsis: concept.slice(colon + 2).trim() || null,
    };
  }
  return { title: concept, synopsis: null };
}

export function resolveOpportunitySetName(
  script: OrderScriptRow | null | undefined,
): string | null {
  const fromStub = script?.script?.opportunity?.setName?.trim();
  if (fromStub) return fromStub;
  const snapshot = script?.scriptContentSnapshot ?? null;
  return (
    snapshotString(snapshot, "opportunitySetName") ??
    snapshotString(snapshot, "setName")
  );
}


function describePersonaBrief(
  snapshot: MarketplaceBriefSnapshot,
  unnamedLabel: string,
): OrderScriptDisplay {
  const selectedHook =
    snapshot.hooks.find((hook) => hook.hookIndex === snapshot.selectedHookIndex) ??
    snapshot.hooks[0] ??
    null;
  const title = snapshot.videoName.trim() || unnamedLabel;
  const hook = selectedHook?.line.trim() || null;
  const synopsis = snapshot.concept.line.trim() || snapshot.concept.direction.trim() || null;
  return {
    title,
    subtitle: hook,
    hook,
    synopsis: distinct(synopsis, title),
    fullScript: null,
    format: null,
    duration: snapshot.duration.trim() || null,
    opportunitySummary:
      snapshot.angle?.trim() ||
      snapshot.opportunitySetName?.trim() ||
      null,
    briefUrl: snapshot.creativeExampleUrl?.trim() || null,
    uploadId: null,
    briefOnly: true,
    personaBrief: snapshot,
    personaName: snapshot.personaTitle?.trim() || null,
    orderInstructions: snapshot.notes?.trim() || null,
    handoffFields: [],
  };
}

/** Resolve what an order script row should show, independent of how it is rendered. */
export function describeOrderScript(row: OrderScriptRow, unnamedLabel: string): OrderScriptDisplay {
  const personaBrief = parseMarketplaceBriefSnapshot(row.scriptContentSnapshot);
  if (personaBrief) return describePersonaBrief(personaBrief, unnamedLabel);
  if (row.briefId) {
    return {
      title: unnamedLabel,
      subtitle: null,
      hook: null,
      synopsis: null,
      fullScript: null,
      format: null,
      duration: null,
      opportunitySummary: null,
      briefUrl: null,
      uploadId: null,
      briefOnly: true,
      personaBrief: null,
      personaName: null,
      orderInstructions: null,
      handoffFields: [],
    };
  }
  const snapshot = row.scriptContentSnapshot ?? null;
  const production = row.production ?? null;
  const concept = isUsableScriptText(row.script?.concept)
    ? row.script!.concept!.trim()
    : isUsableScriptText(production?.concept)
      ? production!.concept!.trim()
      : null;
  const hook = isUsableScriptText(row.script?.hook)
    ? row.script!.hook!.trim()
    : snapshotString(snapshot, "hook") ??
      (isUsableScriptText(production?.hook) ? production!.hook!.trim() : null);
  const briefUrl = snapshotString(snapshot, "briefUrl");
  const uploadId = snapshotString(snapshot, "uploadId");
  const briefOnly = !row.scriptId && Boolean(briefUrl || uploadId);
  const snapshotTitle = snapshotString(snapshot, "title") ?? snapshotString(snapshot, "assetName");
  const snapshotSynopsis = snapshotString(snapshot, "synopsis");
  const fullScript =
    snapshotString(snapshot, "body") ??
    snapshotString(snapshot, "script") ??
    (isUsableScriptText(production?.script) ? production!.script!.trim() : null);
  const format =
    snapshotString(snapshot, "format") ??
    snapshotString(snapshot, "presenter") ??
    (isUsableScriptText(production?.presenter) ? production!.presenter!.trim() : null);
  const duration =
    snapshotString(snapshot, "duration") ??
    (typeof snapshot?.duration === "number" ? String(snapshot.duration) : null) ??
    (isUsableScriptText(production?.duration) ? production!.duration!.trim() : null);
  const opportunitySummary =
    snapshotString(snapshot, "topicTitle") ??
    snapshotString(snapshot, "opportunityTitle") ??
    snapshotString(snapshot, "topicDescription");

  if (briefOnly) {
    const title = snapshotTitle ?? unnamedLabel;
    const synopsis = distinct(
      snapshotSynopsis ?? snapshotString(snapshot, "notes"),
      title,
    );
    return {
      title,
      subtitle: null,
      hook: null,
      synopsis,
      fullScript: distinct(fullScript, snapshotSynopsis),
      format,
      duration,
      opportunitySummary,
      briefUrl,
      uploadId,
      briefOnly,
      personaBrief: null,
      personaName: snapshotPersonaName(snapshot),
      orderInstructions: snapshotOrderInstructions(snapshot, [title, synopsis]),
      handoffFields: snapshotHandoffFields(snapshot),
    };
  }

  const split = concept ? splitConceptTitle(concept) : null;
  const title =
    snapshotTitle ??
    (split && (split.synopsis || split.title.length <= SHORT_TITLE_MAX_CHARS)
      ? split.title
      : null) ??
    concept ??
    hook ??
    unnamedLabel;
  const synopsis = distinct(
    snapshotSynopsis ??
      snapshotString(snapshot, "notes") ??
      (split && split.synopsis && split.title === title ? split.synopsis : null) ??
      (isUsableScriptText(production?.adDescription)
        ? production!.adDescription!.trim()
        : null),
    title,
  );
  const resolvedHook = distinct(hook, title);

  return {
    title,
    subtitle: resolvedHook,
    hook: resolvedHook,
    synopsis,
    fullScript: distinct(fullScript, synopsis ?? title),
    format,
    duration,
    opportunitySummary,
    briefUrl,
    uploadId,
    briefOnly,
    personaBrief: null,
    personaName: snapshotPersonaName(snapshot),
    orderInstructions: snapshotOrderInstructions(snapshot, [
      title,
      resolvedHook,
      synopsis,
    ]),
    handoffFields: snapshotHandoffFields(snapshot),
  };
}
